import { Container, Graphics, Mesh, MeshGeometry, Texture } from 'pixi.js';
import type { GameMap } from '../sim/map';
import type { LandTextures } from './landArt';
import type { TileBounds } from './viewportCull';
import type { TerrainTileInfo } from './terrainLayer';
import { buildTerrainMeshChunks, type AtlasFrame } from './terrainMeshGeometry';

/**
 * The sloped ground of a heighted map (true terrain height, PRD #536 Slice E):
 * a chunked Pixi `Mesh` over the pure buffers of `terrainMeshGeometry.ts`,
 * replacing the per-tile land Sprites while the render-path toggle is on. One
 * mesh per 16x16-tile chunk, all texturing the ONE baked land atlas source
 * (`loadLandTextures` slices every tile name from a single atlas, which is
 * what makes a multi-texture chunk a single draw), corner z interpolated from
 * the neighbouring cells and the same 44x44 land art stretched over each
 * deformed quad - shard-style, no texmap pipeline.
 *
 * ## Cliff faces
 *
 * Corner interpolation makes the surface continuous, so a > CLIMB edge is not
 * an open gap (ADR-0267's deferred artifact) but a steeply stretched quad -
 * that quad IS the cliff face. To make it read as a wall rather than more
 * floor (stretched grass, or a flat-colour square on stub art), each > CLIMB
 * tile gets a translucent dark overlay poly on its own deformed diamond
 * ({@link CLIFF_FACE_COLOR}/{@link CLIFF_FACE_ALPHA}), one `Graphics` per
 * chunk. Degrades gracefully everywhere: on stubs it is simply a darker
 * diamond, on real art a shaded rock-face read.
 */

/** The cliff-face tint: a translucent darkening over the stretched land art. */
export const CLIFF_FACE_COLOR = 0x000000;
export const CLIFF_FACE_ALPHA = 0.35;

export interface MeshTerrainChunk {
  /** The chunk's scene node (mesh + optional cliff overlay), cull target. */
  view: Container;
  mesh: Mesh;
  cliffOverlay?: Graphics;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** The built mesh ground: the sprite path's `Terrain` shape, chunk-culled. */
export interface MeshTerrain {
  container: Container;
  /** Every drawn tile with its cell landZ - the renderer's budget/pick feed. */
  tiles: TerrainTileInfo[];
  chunks: MeshTerrainChunk[];
  /** Toggle chunk visibility by the camera window (undefined = all visible). */
  cull(bounds: TileBounds | undefined): void;
}

/**
 * Build the chunked mesh ground for a heighted grid map. Callers gate on
 * `useMeshTerrainPath(...)` - flat maps and open plains keep their existing
 * paths; this builder assumes a grid map (it iterates width x height).
 */
export function createTerrainMesh(map: GameMap, textures: LandTextures): MeshTerrain {
  // Every land texture is a frame of the one baked atlas; texture the source
  // whole and address tiles by frame-relative UVs. A tile whose texture sits
  // on a DIFFERENT source (impossible via loadLandTextures, possible in a
  // hand-built map) is skipped with the same warn-and-skip degrade as a
  // missing texture, so the chunk stays a single-source draw.
  const first = textures.values().next().value as Texture | undefined;
  const source = first?.source;
  const frameFor = (name: string): AtlasFrame | undefined => {
    const texture = textures.get(name);
    if (!texture) return undefined;
    if (texture.source !== source) {
      console.warn(`land texture "${name}" is not on the shared atlas - skipped by the mesh path`);
      return undefined;
    }
    const f = texture.frame;
    return { x: f.x, y: f.y, w: f.width, h: f.height };
  };

  const container = new Container();
  const tiles: TerrainTileInfo[] = [];
  const chunks: MeshTerrainChunk[] = [];
  if (!source) return { container, tiles, chunks, cull: () => {} };

  const atlasTexture = new Texture({ source });
  for (const data of buildTerrainMeshChunks(map, frameFor, source.width, source.height)) {
    const view = new Container();
    const mesh = new Mesh({
      geometry: new MeshGeometry({
        positions: data.positions,
        uvs: data.uvs,
        indices: data.indices,
      }),
      texture: atlasTexture,
    });
    view.addChild(mesh);
    let cliffOverlay: Graphics | undefined;
    if (data.cliffFaces.length > 0) {
      cliffOverlay = new Graphics();
      for (const face of data.cliffFaces) {
        cliffOverlay.poly(face).fill({ color: CLIFF_FACE_COLOR, alpha: CLIFF_FACE_ALPHA });
      }
      view.addChild(cliffOverlay);
    }
    container.addChild(view);
    tiles.push(...data.tiles);
    chunks.push({
      view,
      mesh,
      cliffOverlay,
      minX: data.minX,
      minY: data.minY,
      maxX: data.maxX,
      maxY: data.maxY,
    });
  }

  const cull = (bounds: TileBounds | undefined): void => {
    for (const c of chunks) {
      c.view.visible =
        !bounds ||
        (c.maxX >= bounds.minX &&
          c.minX <= bounds.maxX &&
          c.maxY >= bounds.minY &&
          c.minY <= bounds.maxY);
    }
  };

  return { container, tiles, chunks, cull };
}
